import React, { useState, useRef, useEffect } from 'react';
import styles from './ImageOverlay.module.css';

const ImageOverlay = ({ mapInstance }) => {
  const [showUploader, setShowUploader] = useState(false);
  const [imageUploaded, setImageUploaded] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageSize, setImageSize] = useState(0.5);
  const [imageRotation, setImageRotation] = useState(0);
  const overlayControlRef = useRef(null);

  useEffect(() => {
    if (mapInstance && imageSrc && mapInstance.loaded() && !imageUploaded) {
      const img = new Image();
      img.onload = () => {
        const imageId = 'custom-svg-' + Date.now();
        try {
          mapInstance.addImage(imageId, img);
          setImageUrl(imageId);
          setImageUploaded(true);
          setShowUploader(false);
          
          // Haritaya point source ve layer ekle
          const center = mapInstance.getCenter();
          const pointId = imageId + '-point';
          
          if (!mapInstance.getSource(pointId)) {
            mapInstance.addSource(pointId, {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: [{
                  type: 'Feature',
                  geometry: {
                    type: 'Point',
                    coordinates: [center.lng, center.lat]
                  }
                }]
              }
            });

            mapInstance.addLayer({
              id: imageId + '-layer',
              type: 'symbol',
              source: pointId,
              layout: {
                'icon-image': imageId,
                'icon-size': imageSize,
                'icon-rotate': imageRotation,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'icon-rotation-alignment': 'viewport',
                'icon-pitch-alignment': 'viewport'
              }
            });
          }
        } catch (error) {
          console.error('Error adding image to map:', error);
        }
      };
      img.src = imageSrc;
    }
  }, [imageSrc, mapInstance, imageUploaded]);

  // Update size and rotation when changed
  useEffect(() => {
    if (mapInstance && imageUrl && mapInstance.loaded() && mapInstance.getLayer(imageUrl + '-layer')) {
      try {
        mapInstance.setLayoutProperty(imageUrl + '-layer', 'icon-size', imageSize);
        mapInstance.setLayoutProperty(imageUrl + '-layer', 'icon-rotate', imageRotation);
      } catch (e) {
        console.log('Update error:', e);
      }
    }
  }, [imageSize, imageRotation, imageUrl, mapInstance]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'image/svg+xml' || file.name.endsWith('.svg'))) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageSrc(event.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert('Lütfen SVG dosyası yükleyin');
    }
  };

  const removeOverlay = () => {
    if (mapInstance && imageUrl && mapInstance.loaded()) {
      try {
        const pointId = imageUrl + '-point';
        const layerId = imageUrl + '-layer';
        
        if (mapInstance.getLayer(layerId)) {
          mapInstance.removeLayer(layerId);
        }
        if (mapInstance.getSource(pointId)) {
          mapInstance.removeSource(pointId);
        }
        if (mapInstance.hasImage(imageUrl)) {
          mapInstance.removeImage(imageUrl);
        }
      } catch (e) {
        console.log('Remove error:', e);
      }
    }
    setImageUploaded(false);
    setImageSrc(null);
    setImageUrl(null);
    setImageSize(0.5);
    setImageRotation(0);
  };

  const handleSizeChange = (delta) => {
    setImageSize(prev => Math.max(0.1, Math.min(3, prev + delta)));
  };

  const handleRotationChange = (delta) => {
    setImageRotation(prev => (prev + delta) % 360);
  };

  return (
    <>
      {/* Upload Button */}
      <button 
        className={styles.uploadButton}
        onClick={() => setShowUploader(true)}
        title="SVG Yükle"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="21"/>
        </svg>
      </button>

      {/* File Input Modal */}
      {showUploader && (
        <div className={styles.uploadModal}>
          <div className={styles.uploadContent}>
            <h3>SVG Yükle</h3>
            <p style={{fontSize: '12px', color: '#666', marginBottom: '10px'}}>SVG dosyanızı seçin</p>
            <input 
              type="file" 
              accept=".svg"
              onChange={handleFileSelect}
            />
            <button 
              onClick={() => setShowUploader(false)} 
              style={{marginTop: '10px', padding: '8px 16px', background: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
            >
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Overlay Control Panel */}
      {imageUploaded && (
        <div ref={overlayControlRef} className={styles.overlayPanel}>
          <div style={{padding: '10px', background: 'rgba(0,0,0,0.8)', color: 'white', borderRadius: '4px', fontSize: '12px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
              <span>SVG Kontrolleri</span>
              <button 
                onClick={removeOverlay} 
                style={{background: 'red', border: 'none', color: 'white', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', marginLeft: '10px'}}
              >
                Kaldır
              </button>
            </div>
            <div style={{display: 'flex', gap: '5px', justifyContent: 'space-between'}}>
              <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                <button onClick={() => handleSizeChange(0.1)} style={{padding: '4px 8px', background: '#555', border: 'none', borderRadius: '4px', cursor: 'pointer', color: 'white'}}>+</button>
                <button onClick={() => handleSizeChange(-0.1)} style={{padding: '4px 8px', background: '#555', border: 'none', borderRadius: '4px', cursor: 'pointer', color: 'white'}}>-</button>
                <span style={{padding: '4px 8px'}}>{Math.round(imageSize * 100)}%</span>
              </div>
              <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                <button onClick={() => handleRotationChange(15)} style={{padding: '4px 8px', background: '#555', border: 'none', borderRadius: '4px', cursor: 'pointer', color: 'white'}}>↻</button>
                <button onClick={() => handleRotationChange(-15)} style={{padding: '4px 8px', background: '#555', border: 'none', borderRadius: '4px', cursor: 'pointer', color: 'white'}}>↺</button>
                <span style={{padding: '4px 8px'}}>{imageRotation}°</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageOverlay;
